/**
 * Garantit `/api` sur la racine d’un backend absolu (`http://host:3001` → `http://host:3001/api`).
 * Sinon Axios appelle `/institution/...` sur le mauvais chemin → 404 « Ressource introuvable ».
 * Ne modifie pas les URL qui ont déjà un chemin (ex. `http://host/api/v1`).
 *
 * Les médias `/uploads/...` sont servis hors préfixe `/api` · voir {@link normalizeUploadsUrlPath}.
 */
/** Ramène `/api/uploads/...` → `/uploads/...` (Express sert les fichiers à la racine, pas sous `/api`). */
export function normalizeUploadsUrlPath(pathOrUrl: string): string {
  const u = pathOrUrl.trim();
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      if (parsed.pathname.startsWith('/api/uploads/')) {
        parsed.pathname = parsed.pathname.replace(/^\/api(?=\/uploads\/)/, '');
        return parsed.toString();
      }
    } catch {
      /* ignore */
    }
    return u;
  }
  if (u.startsWith('/api/uploads/')) return u.replace(/^\/api(?=\/uploads\/)/, '');
  return u;
}

/** Origine du backend sans le suffixe `/api` (fichiers statiques `/uploads/...`). */
export function backendOriginWithoutApi(apiBase: string): string {
  const s = apiBase.replace(/\/+$/, '');
  /** Même origine que la SPA avec `VITE_API_URL=/api` → médias sous `/uploads`, pas `/api/uploads`. */
  if (/^\/api$/i.test(s)) {
    return '';
  }
  if (s.endsWith('/api')) {
    const o = s.slice(0, -4);
    return o.length > 0 ? o : '';
  }
  try {
    const u = new URL(s);
    const pathOnly = (u.pathname || '/').replace(/\/+$/, '') || '/';
    if (pathOnly === '/api') {
      return u.origin;
    }
  } catch {
    /* ignore */
  }
  return s;
}

function normalizeApiBase(raw: string | undefined): string {
  const v = typeof raw === 'string' ? raw.trim() : '';
  if (!v) return '/api';
  const base = v.replace(/\/+$/, '');
  if (/\/api$/i.test(base)) return base;
  if (/^https?:\/\//i.test(base)) {
    try {
      const u = new URL(base);
      const path = (u.pathname || '/').replace(/\/+$/, '') || '/';
      if (path === '/') {
        u.pathname = '/api';
        return u.toString().replace(/\/+$/, '');
      }
      return v;
    } catch {
      return `${base}/api`;
    }
  }
  if (base === '/' || base === '') return '/api';
  const withApi = `${base.replace(/\/$/, '')}/api`;
  return withApi.replace(/\/api\/api$/, '/api');
}

export const config = {
  API_URL: normalizeApiBase(import.meta.env.VITE_API_URL),
  /** Vide en prod same-origin : le client Socket utilise `window.location.origin` (voir `lib/socket.ts`). */
  SOCKET_URL: import.meta.env.VITE_SOCKET_URL || '',
  APP_NAME: 'LyrraStudio',
  APP_DESCRIPTION: 'Plateforme SaaS de formation et création pédagogique',
  APP_VERSION: '1.0.0',

  /** Repli API / en-têtes quand aucune locale n’est encore persistée (aligné sur {@link resolveInitialLanguage}). */
  DEFAULT_LOCALE: 'en' as const,
  SUPPORTED_LOCALES: ['fr', 'en', 'es', 'de', 'it', 'pt', 'lb', 'uk', 'ru', 'ar', 'tr'] as const,

  ROLES: [
    'teacher',
    'institution',
    'admin',
    'drh',
    'rh',
    'manager',
    'collaborator',
    'learner',
  ] as const,

  PAGINATION: {
    DEFAULT_PAGE: 1,
    DEFAULT_PAGE_SIZE: 20,
    MAX_PAGE_SIZE: 100,
    PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  },

  UPLOAD: {
    MAX_FILE_SIZE: 100 * 1024 * 1024, // 100 MB
    MAX_AVATAR_SIZE: 5 * 1024 * 1024, // 5 MB
    MAX_AUDIO_SIZE: 500 * 1024 * 1024, // 500 MB
    MAX_VIDEO_SIZE: 2 * 1024 * 1024 * 1024, // 2 GB
    ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'],
    ALLOWED_AUDIO_TYPES: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/flac', 'audio/aac'],
    ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/webm', 'video/ogg'],
    ALLOWED_DOCUMENT_TYPES: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
    ],
  },

  FEATURES: {
    AI_ASSISTANT: import.meta.env.VITE_ENABLE_AI !== 'false',
    GAMIFICATION: import.meta.env.VITE_ENABLE_GAMIFICATION !== 'false',
    AUDIOBOOKS: import.meta.env.VITE_ENABLE_AUDIOBOOKS !== 'false',
    V2_TRAINING: import.meta.env.VITE_ENABLE_V2_TRAINING === 'true',
    CERTIFICATES: import.meta.env.VITE_ENABLE_CERTIFICATES !== 'false',
    SCORM: import.meta.env.VITE_ENABLE_SCORM !== 'false',
    SOCIAL_LOGIN: true,
    OTP_LOGIN: true,
    DARK_MODE: true,
    ACCESSIBILITY: true,
    MULTI_LANGUAGE: true,
  },

  GAMIFICATION: {
    XP_PER_LESSON: 10,
    XP_PER_QUIZ: 25,
    XP_PER_PROJECT: 50,
    XP_PER_REVIEW: 15,
    XP_PER_STREAK_DAY: 5,
    LEVEL_XP_BASE: 100,
    LEVEL_XP_MULTIPLIER: 1.5,
    MAX_LEVEL: 100,
    STREAK_BONUS_THRESHOLD: 7,
  },

  AUDIO: {
    SAMPLE_RATE: 44100,
    BIT_DEPTH: 16,
    CHANNELS: 2,
    MAX_TRACKS: 32,
    MAX_RECORDING_DURATION: 3600, // 1 hour in seconds
    SUPPORTED_FORMATS: ['mp3', 'wav', 'ogg', 'flac', 'aac'],
  },

  EDUFLOW: {
    MAX_BLOCKS_PER_FLOW: 200,
    MAX_EDGES_PER_FLOW: 500,
    AUTO_SAVE_INTERVAL: 30000, // 30 seconds
    CANVAS_MIN_ZOOM: 0.1,
    CANVAS_MAX_ZOOM: 3,
    CANVAS_DEFAULT_ZOOM: 1,
    GRID_SIZE: 20,
    SNAP_TO_GRID: true,
  },
} as const;

export type SupportedLocale = (typeof config.SUPPORTED_LOCALES)[number];
export type UserRole = (typeof config.ROLES)[number];

export default config;
